/** "编辑图片" entry on a `generate_image` node's Result (VistoeraTASK-00251).
 *
 * NON_DESTRUCTIVE_EDIT / SAVE_AS_NEW_ASSET — this module only decides
 * WHICH already-canonical `SiteAsset` identity backs the CURRENTLY
 * DISPLAYED generation result, so the shared Canvas Image Editor overlay
 * (`CanvasImageEditorOverlay.tsx`, the exact same component/save pipeline
 * `image_asset` nodes already use) can open against it. It never mutates
 * the node, never picks "latest"/"candidate 0"/an origin node's output by
 * mistake, and never needs to download+reupload a temporary copy just to
 * open the editor.
 *
 * `outputAssetId` (when present) IS the canonical `SiteAsset` id already
 * paired with `displayOutputId` by `GenerateImageNode.tsx`'s own
 * `applyCompletedTask` (see that file) — the SAME pair the result `<img>`
 * itself renders from (`resolveImageGenCanvasDisplayUrl`). When it is
 * still `null` (the output has not been materialized into a `SiteAsset`
 * row yet — e.g. auto-materialize backfill hasn't landed for this output
 * by the time this node's local state was captured), the caller must
 * fall back to the canonical, idempotent
 * `POST .../image-generation/outputs/{id}/materialize` bridge instead of
 * any client-side download/re-upload. */

export type GenerateImageEditSourceResolution =
  /** Nothing valid to edit — caller must not open the editor / show the
   * entry at all (mirrors `hasSuccessImage`'s own "no displayed result"
   * case). */
  | { mode: 'unavailable' }
  /** Already-known canonical `SiteAsset` id for the displayed result —
   * open the editor immediately, no network round trip needed. */
  | { mode: 'asset'; assetId: string }
  /** No `SiteAsset` yet for the displayed result — caller must call the
   * canonical materialize bridge for `outputId` and use ITS returned
   * `asset_id` as the editor's `sourceAssetId`. */
  | { mode: 'materialize'; outputId: string }

export function resolveGenerateImageEditSource(args: {
  /** The CURRENTLY DISPLAYED output id — i.e.
   * `resolveGenerationDisplayOutputId({ generatedOutputId, previousOutputId })`,
   * never a hardcoded "latest"/candidate-0/origin-node value. Callers on a
   * multi-candidate/batch canvas must pass THIS node's own displayed
   * output, never a sibling's. */
  displayOutputId: string | null | undefined
  /** This node's own `fields.outputAssetId`, paired with the SAME
   * `displayOutputId` at the moment it was persisted (see
   * `GenerateImageNode.tsx`'s `applyCompletedTask`). */
  outputAssetId: string | null | undefined
}): GenerateImageEditSourceResolution {
  const outputId = args.displayOutputId?.trim() || null
  if (!outputId) return { mode: 'unavailable' }
  const assetId = args.outputAssetId?.trim() || null
  if (assetId) return { mode: 'asset', assetId }
  return { mode: 'materialize', outputId }
}
